import { z } from 'zod';
import {
  ProposedActionSchema,
  type Decision,
  type Profile,
  type ProposedAction,
} from './domain';

export const ConversationChannelSchema = z.enum(['app', 'sms', 'imessage', 'email', 'web']);
export type ConversationChannel = z.infer<typeof ConversationChannelSchema>;

export const MessageTrustSchema = z.enum(['principal', 'trusted-contact', 'external', 'unknown']);
export type MessageTrust = z.infer<typeof MessageTrustSchema>;

export const CompanionIntentSchema = z.enum([
  'CONVERSE',
  'REMIND',
  'READ_MEMORY',
  'WRITE_MEMORY',
  'PROPOSE_ACTION',
  'EXECUTE_ACTION',
]);
export type CompanionIntent = z.infer<typeof CompanionIntentSchema>;

export const CompanionTurnSchema = z.object({
  id: z.string().min(1),
  principalId: z.string().min(1),
  profile: z.enum(['personal', 'professional']),
  channel: ConversationChannelSchema,
  senderId: z.string().min(1),
  trust: MessageTrustSchema,
  intent: CompanionIntentSchema,
  purpose: z.string().min(1),
  dataScopes: z.array(z.string()).default([]),
  sourceRefs: z.array(z.string()).default([]),
  proactive: z.boolean().default(false),
  proactiveOptIn: z.boolean().default(false),
  quietHours: z.boolean().default(false),
  createdAt: z.string().datetime(),
});
export type CompanionTurn = z.infer<typeof CompanionTurnSchema>;

export const CompanionPolicySchema = z.object({
  principalId: z.string().min(1),
  allowedInboundChannels: z.array(ConversationChannelSchema).min(1),
  allowedProactiveChannels: z.array(ConversationChannelSchema).default([]),
  allowedPurposes: z.array(z.string()).default([]),
  allowedDataScopes: z.array(z.string()).default([]),
  allowTrustedContactMemoryWrites: z.boolean().default(false),
});
export type CompanionPolicy = z.infer<typeof CompanionPolicySchema>;

export type CompanionEvaluation = {
  turnId: string;
  profile: Profile;
  decision: Decision;
  reasons: string[];
  tainted: boolean;
  evaluatedAt: string;
};

const isTainted = (trust: MessageTrust) => trust === 'external' || trust === 'unknown';

export function evaluateCompanionTurn(
  turnInput: CompanionTurn,
  policyInput: CompanionPolicy,
  now = new Date(),
): CompanionEvaluation {
  const turn = CompanionTurnSchema.parse(turnInput);
  const policy = CompanionPolicySchema.parse(policyInput);
  const reasons: string[] = [];
  const tainted = isTainted(turn.trust);

  if (turn.principalId !== policy.principalId) {
    return {
      turnId: turn.id,
      profile: turn.profile,
      decision: 'HALT',
      reasons: ['PRINCIPAL_BINDING_MISMATCH'],
      tainted,
      evaluatedAt: now.toISOString(),
    };
  }

  if (!policy.allowedInboundChannels.includes(turn.channel)) reasons.push('CHANNEL_NOT_ALLOWED');
  if (policy.allowedPurposes.length > 0 && !policy.allowedPurposes.includes(turn.purpose)) reasons.push('PURPOSE_NOT_ALLOWED');

  const blockedScopes = turn.dataScopes.filter((scope) => !policy.allowedDataScopes.includes(scope));
  if (blockedScopes.length > 0) reasons.push('DATA_SCOPE_NOT_ALLOWED');

  if (turn.proactive && !turn.proactiveOptIn) reasons.push('PROACTIVE_CONTACT_NOT_CONSENTED');
  if (turn.proactive && !policy.allowedProactiveChannels.includes(turn.channel)) reasons.push('PROACTIVE_CHANNEL_NOT_ALLOWED');

  if (reasons.length > 0) {
    return {
      turnId: turn.id,
      profile: turn.profile,
      decision: 'DENY',
      reasons,
      tainted,
      evaluatedAt: now.toISOString(),
    };
  }

  if (turn.proactive && turn.quietHours) {
    return {
      turnId: turn.id,
      profile: turn.profile,
      decision: 'DEFER',
      reasons: ['QUIET_HOURS'],
      tainted,
      evaluatedAt: now.toISOString(),
    };
  }

  if (turn.intent === 'EXECUTE_ACTION') {
    return {
      turnId: turn.id,
      profile: turn.profile,
      decision: 'DENY',
      reasons: ['MESSAGE_LAYER_HAS_NO_EXECUTION_AUTHORITY'],
      tainted,
      evaluatedAt: now.toISOString(),
    };
  }

  if (turn.intent === 'WRITE_MEMORY' && turn.trust !== 'principal') {
    const trustedWriteAllowed = turn.trust === 'trusted-contact' && policy.allowTrustedContactMemoryWrites;
    if (!trustedWriteAllowed) {
      return {
        turnId: turn.id,
        profile: turn.profile,
        decision: turn.trust === 'unknown' ? 'DENY' : 'STEP_UP',
        reasons: [tainted ? 'EXTERNAL_CONTENT_TAINTED' : 'NON_PRINCIPAL_MEMORY_WRITE'],
        tainted,
        evaluatedAt: now.toISOString(),
      };
    }
  }

  if (turn.intent === 'PROPOSE_ACTION' && tainted) {
    return {
      turnId: turn.id,
      profile: turn.profile,
      decision: 'MODIFY',
      reasons: ['EXTERNAL_CONTENT_TAINTED', 'PROPOSAL_ONLY'],
      tainted,
      evaluatedAt: now.toISOString(),
    };
  }

  return {
    turnId: turn.id,
    profile: turn.profile,
    decision: 'ALLOW',
    reasons: ['WITHIN_COMPANION_POLICY'],
    tainted,
    evaluatedAt: now.toISOString(),
  };
}

export function buildGovernedAction(turnInput: CompanionTurn, actionInput: ProposedAction): ProposedAction {
  const turn = CompanionTurnSchema.parse(turnInput);
  const action = ProposedActionSchema.parse(actionInput);

  if (turn.intent !== 'PROPOSE_ACTION') throw new Error('COMPANION_TURN_IS_NOT_ACTION_PROPOSAL');
  if (action.principalId !== turn.principalId) throw new Error('PRINCIPAL_BINDING_MISMATCH');
  if (action.profile !== turn.profile) throw new Error('PROFILE_BOUNDARY');

  const evidence = new Set([
    ...action.evidence,
    `companion-turn:${turn.id}`,
    ...turn.sourceRefs,
  ]);

  if (isTainted(turn.trust)) evidence.add('taint:external');

  return ProposedActionSchema.parse({
    ...action,
    purpose: turn.purpose,
    dataScopes: [...new Set([...action.dataScopes, ...turn.dataScopes])],
    evidence: [...evidence],
    createdAt: action.createdAt ?? turn.createdAt,
  });
}
